import {
  Stack,
  IconButton,
  Modal,
  CircularProgress,
  Typography,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import CloseIcon from '@mui/icons-material/Close'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import { useCallback, useState } from 'react'
import { Prescription } from '../../../types/models/Presc'
import { CustomButton } from '../../../components/molecules/CustomButton'
import { useAppType } from '../../../contexts/AppTypeContext'
import { useReportCallbacks } from '../../../shared'
import { useToast } from '../../../contexts/ToastContext'

type Params = {
  prescription: Prescription | undefined
}

/**
 * 処方箋報告フック（TanStack Query Mutation 版）
 *
 * 報告成功時は invalidateQueries により処方箋リストが自動更新される。
 * コアのレポート機能は useReportCallbacks に委譲。
 */
export const useReportPresc = ({ prescription }: Params) => {
  const { config } = useAppType()
  const [isOtherReportModalOpen, setIsOtherReportModalOpen] =
    useState<boolean>(false)
  const { showToast } = useToast()

  // 共通フックからレポートコールバックを取得
  const {
    reportBasicResult,
    reportRPUnit,
    reportMedication,
    reportRPIndex,
    reportSpecificError: reportSpecificErrorBase,
    isSending,
  } = useReportCallbacks({
    prescription,
    onReportSpecificErrorSuccess: () => {
      setIsOtherReportModalOpen(false)
      showToast(config.shortServiceName + 'への報告が完了しました。', 'success')
    },
  })

  // reportSpecificError をラップ（共通フックの onSuccess で UI 更新）
  const reportSpecificError = useCallback(
    async (ocrErrorLabel: string) => {
      await reportSpecificErrorBase(ocrErrorLabel)
    },
    [reportSpecificErrorBase]
  )

  const shortServiceName = config.shortServiceName

  // Patch
  const inferenceErrorTypes = [
    { type: 'med_shortage', displayLabel: '薬剤に欠損がある' },
    { type: 'med_mistake', displayLabel: '薬剤に誤りがある' },
    { type: 'youhou_mistake', displayLabel: '用法に誤りがある' },
    {
      type: 'chouzai_youryou_mistake',
      displayLabel: '日数・用量に誤りがある',
    },
    {
      type: 'chouzai_youryou_shortage',
      displayLabel: '日数・用量が「0」になっている',
    },
  ]

  const ReportSendingOverlay = isSending && (
    <Stack
      sx={{
        zIndex: 1305,
        position: 'absolute',
        bgcolor: 'rgb(0,0,0, 0.4)',
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      spacing={2}
    >
      <CircularProgress style={{ width: '100px', height: '100px' }} />
      <Typography sx={{ color: '#fff', fontSize: '24px' }}>
        報告送信中...
      </Typography>
    </Stack>
  )

  const renderSnackBar = () => (
    <div>
      <Modal
        open={isOtherReportModalOpen}
        onClose={() => setIsOtherReportModalOpen(false)}
      >
        <MuiAlert
          elevation={10}
          variant="filled"
          iconMapping={{
            success: (
              <CampaignRoundedIcon style={{ fontSize: 40, color: 'black' }} />
            ),
          }}
          sx={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '24px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            width: 600,
          }}
          style={{
            backgroundColor: 'white',
            color: 'black',
          }}
        >
          <IconButton
            aria-label="close"
            color="inherit"
            onClick={() => setIsOtherReportModalOpen(false)}
            style={{ position: 'absolute', top: '0', right: '0' }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          <Stack direction="column" alignItems="center">
            <Typography variant="h5" sx={{ pb: 2 }}>
              {'その他' + shortServiceName + 'への報告'}
            </Typography>
            <Stack>
              {inferenceErrorTypes.map((item) => (
                <Stack key={item.type} p={1}>
                  <CustomButton
                    variant="contained"
                    sx={{ width: 400, height: 50 }}
                    onClick={() => reportSpecificError(item.type)}
                  >
                    {item.displayLabel}
                  </CustomButton>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </MuiAlert>
      </Modal>
    </div>
  )

  // Patch
  const openModal = useCallback(() => {
    // TMNアプリでは常に「その他報告」モーダルを表示
    setIsOtherReportModalOpen(true)
  }, [])

  return {
    reportBasicResult,
    reportRPUnit,
    reportMedication,
    reportRPIndex,
    openModal,
    renderSnackBar,
    ReportSendingOverlay,
  }
}
